import { useCallback, useContext, memo } from 'react';
import styled from '@emotion/styled';
import { CapabilitiesContext } from '@/components/BrandInsights/context';
import { ChatResponsePane } from '@/components/Chat/ResponsePane';
import { StarRating, useUpdateQueryRatingMutation } from '@/components/Chat.Feedback';
import { OpenQueryFeedbackModalContext } from '@/components/Chat.Feedback/context';
import { MessageCitations } from '@/components/Chat.Message.Citations';
import { ChatHintType, ChatResponseType } from '@/enums/chat';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import type { Chat } from '@/types';
import { getQueryResponseAsText } from '@/utils';
import { useShareChatModal } from '../Chat.History/Modal.ShareChat';
import { getCitationsDisplayCount } from '../Chat.Message.Citations/utils';
import { ActiveChatSessionContext, MessageCitationsToggleContext } from './context';
import { ChartRefContext } from './context';
import { useChatPermissions } from './hooks';
import { MessageButtons } from './Message.Buttons';
import { CopyButton } from './Message.CopyToClipboard';
import { DownloadButton } from './Message.Download';
import { GoToTraceUrl } from './Message.GoToTraceUrl';
import { ReadAloudButton } from './Message.ReadAloud';
import { ShareButton } from './Message.ShareChatThread';
import { RelatedQuestionsPane } from './QueryHints';

type Props = {
  item: Chat.Query;
  numCitations: number;
};

export const SystemUserMessageFooter = ({ item, numCitations }: Props) => {
  const chat = useContext(ActiveChatSessionContext);
  const chatPermissions = useChatPermissions();
  const capabilities = useContext(CapabilitiesContext);
  const [citationsOpen, toggleCitationsOpen] = useContext(MessageCitationsToggleContext);
  const [ref] = useContext(ChartRefContext);

  const { mutateAsync: updateQueryRating } = useUpdateQueryRatingMutation({
    chatInstanceId: chat.id,
    queryIdentifier: item.identifier,
  });

  const openFeedbackModal = useContext(OpenQueryFeedbackModalContext);

  const copyToClipboard = useCopyToClipboard();

  const [toggleShareModel, ShareModal] = useShareChatModal();

  const handleShareChatThread = useCallback(() => {
    toggleShareModel();
  }, [toggleShareModel]);


  const handleRatingClicked = useCallback((value: Chat.Rating) => {
    if (value <= 3) {
      return openFeedbackModal({
        queryIdentifier: item.identifier,
        chatInstanceId: chat.id,
        rating: value,
      });
    }

    return updateQueryRating(value);
  }, [
    item.identifier,
    updateQueryRating,
    openFeedbackModal,
    chat.id,
  ]);

  const handleCopy = useCallback(() => {
    copyToClipboard(getQueryResponseAsText(item.response));
  }, [copyToClipboard, item.response]);

  const view = {
    rating: chatPermissions.canModify,
    followUps: chatPermissions.canModify && !!item.suggestedFollowups.length,
    trace: capabilities.canViewTraces && !!item.langfuseTraceUrl,
  };

  return (
    <>
      <Layer2>
        <ActionsContainer>
          <ActionsLeft>
            <ShareButton onClick={handleShareChatThread} />
            <ShareModal chatInstance={chat} />

            <MessageButtons item={item} />
            <CopyButton onClick={handleCopy} />

            {item.response?.type === ChatResponseType.Chart && ref &&
              <DownloadButton onClick={() => console.log('Download button clicked!')} />}

            <ReadAloudButton
              queryId={item.id} />
          </ActionsLeft>

          <ActionsRight>
            {view.rating && (
              <StarRating
                value={item.rating}
                onChange={handleRatingClicked}
              />
            )}
            {view.trace && <GoToTraceUrl url={item.langfuseTraceUrl} />}
          </ActionsRight>
        </ActionsContainer>
      </Layer2>
      {!!numCitations && (
        <StyledCitationsPane
          title={`Sources (${item.citations.reduce((total, citation) => {
            const amt = citation.snippets.length > 0 ? citation.snippets.length : 1;
            return total + amt;
          }, 0)})`}
          open={citationsOpen}
          toggleOpen={toggleCitationsOpen}
        >
          <StyledMessageCitations items={item.citations} />
        </StyledCitationsPane>
      )}
      {view.followUps && (
        <StyledRelatedQuestions
          hints={item.suggestedFollowups.map(f => ({
            displayValue: f,
            type: ChatHintType.Text,
          }))}
        />
      )}
    </>
  );
};

type ContainerProps = {
  className?: string;
} & ChildrenProps;

export const SystemUserMessageFooterContainer = memo(({ className, children }: ContainerProps) => {
  return (
    <Root className={className}>
      <Wrap>
        {children}
      </Wrap>
    </Root>
  );
});

const Root = styled.div`
  margin-top: 15px;
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledRelatedQuestions = styled(RelatedQuestionsPane)`
  margin-top: 15px;
`;

const StyledCitationsPane = styled(ChatResponsePane)`
  margin-top: 15px;
`;

const StyledMessageCitations = styled(MessageCitations)`
  margin-top: 10px;
`;

const Layer2 = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const ActionsContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

const ActionsLeft = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
});

const ActionsRight = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
});